import { Fragment, useState } from 'react';

import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@frontierag/frontier-ui-library/icons';

import {
  Box,
  IconButton,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@frontierag/frontier-ui-library';
import { t } from 'i18next';
import get from 'lodash/get';

import { styles } from './styles';
import type { ITable } from './types';

const Table = ({
  columns = [],
  rows,
  labelNoRecords = t('label.noRecordsFound'),
  displayNoRecords = true,
  collapsibleTableData,
  sx,
}: ITable) => {
  const [openedRows, setOpenedRows] = useState<any>([]);

  const toggleTableDetails = (index: number) => {
    if (openedRows.includes(index)) {
      const newOpenedRows = openedRows.filter((opened) => opened !== index);
      setOpenedRows(newOpenedRows);
    } else {
      setOpenedRows((prev) => [...prev, index]);
    }
  };

  return (
    <TableContainer>
      <MuiTable
        sx={[
          collapsibleTableData ? styles.expandableTable : {},
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        <TableHead>
          <TableRow>
            {collapsibleTableData && <TableCell sx={styles.expandCell} />}
            {Array.isArray(columns) &&
              columns.length > 0 &&
              columns.map((col, index) => (
                <TableCell key={index} variant="head" {...(col.props || {})}>
                  <Typography variant="label2">{col.title}</Typography>
                </TableCell>
              ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {Boolean(Array.isArray(rows) && rows.length > 0) &&
            rows.map((row, index) => (
              <Fragment key={`table-row-${index}`}>
                <TableRow
                  className="table-row"
                  key={index}
                  sx={
                    collapsibleTableData ? (index % 2 === 0 ? styles.rowEven : styles.rowOdd) : {}
                  }
                >
                  {collapsibleTableData && (
                    <TableCell key={`expandable-cell-${index}`} sx={styles.expandCell}>
                      <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => toggleTableDetails(index)}
                        sx={{ color: 'naval.L40' }}
                      >
                        {openedRows.includes(index) ? (
                          <KeyboardArrowUpIcon fontSize="small" />
                        ) : (
                          <KeyboardArrowDownIcon fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                  )}

                  {Array.isArray(columns) &&
                    columns.length > 0 &&
                    columns.map((col, index) =>
                      col.actions ? (
                        Array.isArray(col.actions) &&
                        col.actions.length > 0 && (
                          <TableCell
                            variant="body"
                            align="right"
                            size="small"
                            key={`${col.field}-${index}`}
                            {...(col.cellProps || {})}
                          >
                            <Box display="flex" justifyContent="right" gap={1}>
                              {col.actions.map((action, index) => (
                                <Tooltip
                                  title={action.title}
                                  key={`tooltip-${action.title}-${index}`}
                                >
                                  <span>
                                    <IconButton
                                      {...(action.props ? action.props(row) : {})}
                                      key={index}
                                      onClick={() => action.onActionClick(row)}
                                      size="small"
                                    >
                                      {typeof action.icon === 'function'
                                        ? action.icon(row)
                                        : action.icon}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              ))}
                            </Box>
                          </TableCell>
                        )
                      ) : (
                        <TableCell
                          key={`${col.field}-${index}`}
                          variant="body"
                          {...(col.cellProps || {})}
                        >
                          <Typography variant="body2">{get(row, col.field, '')}</Typography>
                        </TableCell>
                      ),
                    )}
                </TableRow>
                {collapsibleTableData && openedRows.includes(index) && (
                  <TableRow
                    className="table-row"
                    sx={index % 2 === 0 ? styles.rowEven : styles.rowOdd}
                    key={`collapsible-row-${index}`}
                  >
                    <TableCell
                      colSpan={columns.length + 1}
                      className="table-cell collapsible-row-cell-details"
                      sx={styles.tableDetailsCell}
                    >
                      {collapsibleTableData([row])}
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}

          {Boolean(Array.isArray(rows) && rows.length === 0 && displayNoRecords) && (
            <TableRow>
              <TableCell colSpan={columns.length + 1} sx={{ textAlign: 'center' }}>
                {labelNoRecords}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </MuiTable>
    </TableContainer>
  );
};

export default Table;
